// DrilldownHeatmapDynamic.jsx
// A fully-dynamic Highcharts Maps drilldown heatmap (World -> Continent -> Country -> Region/State)
// Option A: Plain React (JSX). Drop into any React app (create-react-app, Next.js client-side, etc.)
import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import MapModule from "highcharts/modules/map";

// initialize maps module
// MapModule(Highcharts);

if (typeof MapModule === "function") {
  MapModule(Highcharts);
}

// CONSTANTS
const HC_BASE = "https://code.highcharts.com/mapdata";
const CACHE_PREFIX = "hc_map_cache_v1_";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours cache

// utility: fetch json and throw on error
async function fetchJsonOrThrow(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} - ${url}`);
  return res.json();
}

// Cache wrapper (localStorage)
function readCache(key) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.ts || Date.now() - obj.ts > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return obj.data;
  } catch (e) {
    console.warn("Cache read error", e);
    return null;
  }
}
function writeCache(key, data) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch (e) {
    console.warn("Cache write error", e);
  }
}

// tries multiple candidate urls and returns first success
async function tryUrls(urls = []) {
  let lastErr = null;
  for (const u of urls) {
    try {
      const j = await fetchJsonOrThrow(u);
      return { json: j, url: u };
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("No URLs available");
}

// Extract likely ISO2 code from properties (common property keys)
function extractIso2FromFeatureProps(props = {}) {
  return (
    props["iso-a2"] ||
    props["iso_a2"] ||
    props["iso2"] ||
    props["ISO_A2"] ||
    (props["hc-key"] && typeof props["hc-key"] === "string"
      ? props["hc-key"].split("-")[0]
      : null) ||
    null
  );
}

// Build map CDN paths for common needs
function pathForWorldContinents() {
  // Highcharts provides a continents map under custom/world-continents.geo.json
  return `${HC_BASE}/custom/world-continents.geo.json`;
}
function pathForWorld() {
  return `${HC_BASE}/custom/world.geo.json`;
}
function pathForContinent(continentSlug) {
  // some continent data exist under custom/<continent>.geo.json (e.g., africa, asia)
  return `${HC_BASE}/custom/${continentSlug}.geo.json`;
}
function pathForCountryIsoAll(iso2Lower) {
  // common pattern: countries/{iso2}/{iso2}-all.geo.json
  return `${HC_BASE}/countries/${iso2Lower}/${iso2Lower}-all.geo.json`;
}

// Helper: joinBy key fallback detection
function detectJoinKey(geojson) {
  // examine first feature properties to find 'hc-key' or other
  if (!geojson || !geojson.features || !geojson.features.length)
    return "hc-key";
  const p = geojson.features[0].properties || {};
  if ("hc-key" in p) return "hc-key";
  if ("iso-a2" in p) return "iso-a2";
  if ("iso_3166_2" in p) return "iso_3166_2";
  if ("id" in p) return "id";
  return "hc-key";
}

// Simple spinner
function Spinner() {
  return (
    <div style={{ padding: 12, fontSize: 14, color: "#333" }}>
      Loading map...
    </div>
  );
}

// Main component
export default function DrilldownHeatmapDynamic({
  // optional props
  initialLevel = "continents", // 'continents' or 'world'
  autoCache = true,
  getDataForMap = null, // optional async (level, parentMeta, features) => [{key, value, name}, ...]
}) {
  const chartRef = useRef(null);

  // stack entry = { level: 'continents'|'world'|'country'|'region', id, name, meta }
  const [stack, setStack] = useState([
    { level: initialLevel, id: initialLevel, name: "World" },
  ]);
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seriesData, setSeriesData] = useState([]);
  const [title, setTitle] = useState("World");
  const [cityBubbles, setCityBubbles] = useState([]);

  useEffect(() => {
    // load initial
    loadForStackTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadForStackTop() {
    const top = stack[stack.length - 1];
    await loadLevel(top);
  }

  async function loadLevel(stackItem) {
    setLoading(true);
    setError(null);

    try {
      let geo = null;
      // caching key
      const cacheKey = `map_${stackItem.id}`;

      if (autoCache) {
        const cached = readCache(cacheKey);
        if (cached) {
          geo = cached;
        }
      }

      if (!geo) {
        // decide fetch strategy by level
        if (stackItem.level === "continents") {
          // world continents
          const url = pathForWorldContinents();
          const res = await tryUrls([url, pathForWorld()]);
          geo = res.json;
        } else if (stackItem.level === "world") {
          const res = await tryUrls([pathForWorld()]);
          geo = res.json;
        } else if (stackItem.level === "continent") {
          // attempt continent-specific map, else fallback to world filtered
          const slug = stackItem.meta && stackItem.meta.slug;
          if (slug) {
            try {
              const res = await tryUrls([
                pathForContinent(slug),
                pathForWorld(),
              ]);
              geo = res.json;
            } catch {
              const res = await tryUrls([pathForWorld()]);
              geo = res.json;
            }
          } else {
            // fallback to world
            const res = await tryUrls([pathForWorld()]);
            geo = res.json;
          }
        } else if (stackItem.level === "country") {
          // try country iso-all map
          const iso = stackItem.meta && stackItem.meta.iso2;
          if (iso) {
            try {
              const res = await tryUrls([
                pathForCountryIsoAll(iso.toLowerCase()),
              ]);
              geo = res.json;
            } catch (e) {
              // country admin map not available - fallback to world and filter
              const res = await tryUrls([pathForWorld()]);
              geo = res.json;
            }
          } else {
            // fallback to world
            const res = await tryUrls([pathForWorld()]);
            geo = res.json;
          }
        } else {
          // generic fallback to world
          const res = await tryUrls([pathForWorld()]);
          geo = res.json;
        }
        if (autoCache) writeCache(cacheKey, geo);
      }

      // If continent-level and using world map, attempt best-effort filter of countries based on continent name
      if (
        stackItem.level === "continent" &&
        geo &&
        geo.features &&
        stackItem.meta &&
        stackItem.meta.name
      ) {
        // Attempt to keep only countries matching continent property if possible
        const continentName = stackItem.meta.name.toString().toLowerCase();
        const filtered = geo.features.filter((f) => {
          const p = f.properties || {};
          const val = (
            p.continent ||
            p.CONTINENT ||
            p.region ||
            p.REGION ||
            p.admin ||
            p.ADMIN ||
            p.CONTINENT_NAME ||
            ""
          )
            .toString()
            .toLowerCase();
          // try either property match or presence of continentName inside name
          return (
            val.includes(continentName) ||
            (p.name || "").toString().toLowerCase().includes(continentName)
          );
        });
        if (filtered && filtered.length > 0) {
          geo = { ...geo, features: filtered };
        }
      }

      // register in Highcharts.maps for possible reuse
      try {
        if (geo && stackItem.id) Highcharts.maps[stackItem.id] = geo;
      } catch (e) {
        // ignore
      }

      setGeojson(geo);
      setTitle(stackItem.name || stackItem.id || "Map");
      // prepare series data (use getDataForMap if provided)
      let data = [];
      if (typeof getDataForMap === "function") {
        // allow user-provided async data fetch
        try {
          const userData = await getDataForMap(
            stackItem.level,
            stackItem.meta || null,
            geo.features || []
          );
          // expected shape: [{ key, value, name }, ...]
          data = (userData || []).map((d) => ({
            "hc-key": d.key,
            value: d.value,
            name: d.name,
          }));

           let bubbleCities = [];
          if (userData && userData.cities) {
            bubbleCities = userData.cities.map((c) => ({
              name: c.name,
              lat: c.lat,
              lon: c.lon,
              z: c.value, // bubble size
              value: c.value,
            }));
          }

          setCityBubbles(bubbleCities);
        } catch (e) {
          console.warn("User data fetch error", e);
          data = [];
        }

        /// for actual api
        // const formatted = await loadLevelData(stackItem.level, stackItem.meta?.iso2);

        // data = formatted;
      }

      // fallback: create random values mapped to join key
      if (!data || data.length === 0) {
        const joinKey = detectJoinKey(geo);
        data = (geo.features || []).map((f, i) => {
          const props = f.properties || {};
          const key =
            props[joinKey] ||
            props["hc-key"] ||
            props["iso-a2"] ||
            props["id"] ||
            `k-${i}`;
          return {
            "hc-key": key,
            value: Math.round(Math.random() * 1000),
            name: props.name || props.NAME || `Area ${i}`,
          };
        });
      }

      setSeriesData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message || String(err));
      setLoading(false);
      setGeojson(null);
      setSeriesData([]);
      console.error("Map load error", err);
    }
  }

  // when a map area is clicked -> attempt to drilldown
  async function onPointClick(point) {
    try {
      if (!point || !point.properties) return;
      const props = point.properties || {};
      const name =
        point.name || props.name || props.admin || props.ADMIN || "Area";
      // detect iso2 for country fetch
      const iso2 = extractIso2FromFeatureProps(props);

      // build new stack item based on current
      const current = stack[stack.length - 1];
      if (!current) return;

      // decide next level
      if (current.level === "continents") {
        // clicking continent: next -> continent (list of countries in that continent)
        // attempt to get slug: many continents maps have 'hc-key' like 'asia' etc or property 'code'
        const slug = (
          props["hc-key"] ||
          props["code"] ||
          (name || "").toLowerCase()
        )
          .toString()
          .toLowerCase()
          .replace(/\s+/g, "-");
        const next = {
          level: "continent",
          id: `continent-${slug}`,
          name: name,
          meta: { slug, name },
        };
        setStack((s) => [...s, next]);
        await loadLevel(next);
        return;
      } else if (current.level === "continent" || current.level === "world") {
        // clicking a country -> try load that country's admin map
        // need iso2; if not found, attempt to parse hc-key
        let iso = iso2 || (props["hc-key"] && props["hc-key"].split("-")[0]);
        if (iso) iso = iso.toLowerCase();
        const next = {
          level: "country",
          id: `country-${iso || name.toLowerCase().replace(/\s+/g, "-")}`,
          name: name,
          meta: { iso2: iso, name },
        };
        setStack((s) => [...s, next]);
        await loadLevel(next);
        return;
      } else if (current.level === "country") {
        // if user clicks inside country admin map, we could try another deeper region level (admin2). For simplicity we treat country map as deepest in this component.
        // Optionally: implement admin2 fetch if data exists (not implemented by default).
        // For now: do nothing or show a message
        // You may extend this path to attempt admin2 maps (country/{iso}/{iso}-all.geo.json often includes admin1)
        console.info(
          "Reached country map - deeper admin levels can be added if available."
        );
        return;
      }
    } catch (e) {
      console.error("Drilldown click handling error", e);
    }
  }

  function goBack() {
    if (stack.length <= 1) return;
    const newStack = stack.slice(0, -1);
    setStack(newStack);
    // load the new top
    loadLevel(newStack[newStack.length - 1]);
  }
  // const [currentLevel, setCurrentLevel] = useState("continent");

  const continentLeads = {
    africa: 120,
    asia: 450,
    europe: 300,
    "north-america": 180,
    "south-america": 90,
    oceania: 60,
    antarctica: 0,
  };

  const asiaCountryLeads = {
    PK: 180,
    IN: 220,
    CN: 50,
    BD: 0,
    JP: 0,
  };

  const pakistanStateLeads = {
    pb: 90, // Punjab
    sd: 45, // Sindh
    kp: 30, // Khyber Pakhtunkhwa
    ba: 15, // Balochistan (BA or BL depending on map)
    is: 0, // Islamabad
  };

  const indiaStateLeads = {
    mh: 120, // Maharashtra
    dl: 80, // Delhi
    ka: 60, // Karnataka
    pb: 30, // Punjab
    up: 10, // Uttar Pradesh
  };

  // Highcharts options builder
  const options = {
    chart: {
      map: geojson || undefined,
      backgroundColor: "transparent",
      height: 620,
    },
    title: {
      text: title,
    },
    mapNavigation: {
      enabled: true,
      enableDoubleClickZoomTo: true,
    },
    colorAxis: {
      min: 0,
      nullColor: "#e5e7eb", // gray for 0 values
      max: Math.max(1000, ...seriesData.map((d) => d.value || 0)),
      type: "linear",
      stops: [
        [0, "#f7fbff"],
        [0.5, "#6baed6"],
        [1, "#08306b"],
      ],
    },
    tooltip: {
      formatter: function () {
        const p = this.point || {};
        return `<strong>${
          p.name || (p.properties && p.properties.name) || "Area"
        }</strong><br/>Leads: ${p.value != null ? p.value : "—"}`;
      },
      useHTML: true,
    },
    series: [
      {
        data: seriesData,
        mapData: geojson || undefined,
        joinBy: detectJoinKey(geojson) || "hc-key",
        name: "leads",
        states: {
          hover: {
            color: "#FFAA00",
          },
        },
        allAreas: true,
        point: {
          //   events: {

          //      click: function () {
          //   if (this.value === 0) return; // not clickable

          //   if (currentLevel === "continent") {
          //     setCurrentLevel("country");
          //     loadLevelData("country", this["hc-key"].toUpperCase());
          //   }

          //   else if (currentLevel === "country") {
          //     setCurrentLevel("state");
          //     loadLevelData("state", this["hc-key"].toUpperCase());
          //   }
          // }
          //   },
          // dynamic cursor via mouseOver event
          events: {
            mouseOver: function () {
              if (!this.value || this.value === 0) {
                this.series.chart.container.style.cursor = "not-allowed";
              } else {
                this.series.chart.container.style.cursor = "pointer";
              }
            },
            mouseOut: function () {
              this.series.chart.container.style.cursor = "default";
            },
            click: function () {
              if (!this.value || this.value === 0) return;
              onPointClick(this);
            },
          },
        },
      },
      // 🔵 NEW: City Bubble Layer (only for country level)
      //   ...(isCountryLevel
      //     ? [
      //         {
      //           type: "mapbubble",
      //           name: "Cities",
      //           color: "#2563eb",
      //           minSize: 10,
      //           maxSize: 45,
      //           tooltip: {
      //             pointFormat: "<b>{point.name}</b><br/>Leads: {point.value}",
      //           },
      //           data: (
      //             cityBubbleData[
      //               stack[stack.length - 1].meta.iso2.toUpperCase()
      //             ] || []
      //           ).map((c) => ({
      //             name: c.name,
      //             lat: c.lat,
      //             lon: c.lon,
      //             value: c.value,
      //             z: c.value, // Highcharts bubble size uses `z`
      //           })),
      //         },
      //       ]
      //     : []),
      {
        type: "mapbubble",
        name: "Cities",
        data: cityBubbles,
        minSize: 5,
        maxSize: "12%",
        tooltip: {
          pointFormat: "{point.name}: {point.value}",
        },
      },
    ],
    credits: { enabled: false },
  };

  /// for my use

  async function loadLevelData(level, code = null) {
    let url = "";

    //   if (level === "continent") url = "/api/continents";
    //   if (level === "country") url = `/api/continents/${code}`;
    //   if (level === "state") url = `/api/countries/${code}`;

    //   const res = await fetch(url);
    //   const data = await res.json();

    let data;

    // console.log("level", level );

    // // When your real API is ready — replace these 3 blocks:
    // if (level === "continent") {
    //   data = continentLeads; // fake
    // }

    // if (level === "country") {
    //   data = countryLeads; // fake
    // }

    // if (level === "state") {
    //   data = stateLeads; // fake
    // }

    // 🔹 Level 1: World → Continents
    if (level === "continents") {
      data = continentLeads; // africa, asia, europe...
    }

    // 🔹 Level 2: Continent → Countries
    if (level === "continent") {
      if (code === "asia" || code === "AS" || code === "as") {
        data = asiaCountryLeads; // PK, IN, CN...
      } else {
        data = {}; // Other continents not implemented
      }
    }

    // 🔹 Level 3: Country → States
    if (level === "country") {
      if (code === "PK" || code === "pk") {
        data = pakistanStateLeads; // pb, sd, kp...
      } else if (code === "IN" || code === "in") {
        data = indiaStateLeads; // mh, dl, ka...
      } else {
        data = {}; // Other countries not implemented
      }
    }

    // Format for Highcharts
    return Object.keys(data).map((key) => ({
      "hc-key": key.toLowerCase(),
      value: data[key],
    }));
  }

  return (
    <div
      style={{ width: "100%", maxWidth: 1200, margin: "0 auto", padding: 12 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Drilldown Heatmap</h3>
          <div style={{ fontSize: 13, color: "#666" }}>
            {stack.map((s) => s.name).join(" › ")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={goBack}
            disabled={stack.length <= 1}
            style={{ padding: "6px 10px", borderRadius: 6 }}
          >
            ← Back
          </button>
          <div style={{ fontSize: 13, color: "#333" }}>
            Level: {stack.length} / 3
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 8,
            borderRadius: 6,
            background: "#ffecec",
            color: "#900",
            marginBottom: 8,
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {loading && <Spinner />}
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={"mapChart"}
          options={options}
          ref={chartRef}
        />
      </div>

      <div style={{ marginTop: 10, color: "#555", fontSize: 13 }}>
        <strong>Notes:</strong>
        <ul>
          <li>
            Maps are loaded from Highcharts CDN
            (code.highcharts.com/mapdata/...).
          </li>
          <li>
            Country admin maps may not exist for tiny countries — fallback to
            country-level view will show.
          </li>
          <li>
            To supply real values, pass{" "}
            <code>getDataForMap(level, meta, features)</code> prop that returns
            array of <code>{`{key, value, name}`}</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
